<script>
	import { createEventDispatcher, onMount, tick } from 'svelte';
	import { injectGlobalStyles } from './shared.js';
	import {
		clearAuthorization,
		getStoredAuthorization,
		storeAuthorization,
		apiRequest
	} from './auth.js';
	import Loader from './Loader.svelte';
	import WidgetSelect from './WidgetSelect.svelte';

	export let apiUrl = '';
	export let open = false;
	export let inline = false;
	export let eyebrow = 'Личный кабинет';
	export let title = 'Добро пожаловать';
	export let intro = 'Войдите по номеру телефона, чтобы участвовать в конкурсах прогнозов.';

	const dispatch = createEventDispatcher();

	const providers = [
		{ value: 1, label: 'SMS', hint: 'Код будет отправлен в SMS на указанный номер.' },
		{ value: 2, label: 'MAX', hint: 'Код будет отправлен в MAX-аккаунт, связанный с номером.' },
		{ value: 3, label: 'Звонок', hint: 'Вы получите звонок с кодом подтверждения.' },
		{ value: 4, label: 'Telegram', hint: 'Код будет отправлен в Telegram, связанный с номером.' }
	];

	let host;
	let stage = 'phone';
	let phone = '';
	let personalDataConsent = false;
	let provider = '4';
	let verification = null;
	let authorization = null;
	let profile = emptyProfile();
	let codeDigits = ['', '', '', ''];
	let pinInputs = [];
	let errorMessage = '';
	let savingProfile = false;
	let wasOpen = false;

	$: normalizedPhone = phone.replace(/\D/g, '');
	$: selectedProvider = providers.find((item) => item.value === Number(provider)) || providers[3];

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) injectGlobalStyles(root);
		if (inline) begin();
	});

	// При открытии модального окна начинаем новый поток авторизации.
	$: if (open !== wasOpen) {
		wasOpen = open;
		if (open && !inline) begin();
	}

	function emptyProfile() {
		return {
			firstName: '',
			lastName: '',
			surName: '',
			birthday: '',
			gender: 0,
			phone: ''
		};
	}

	function resetFlow() {
		stage = 'phone';
		phone = '';
		personalDataConsent = false;
		provider = '4';
		verification = null;
		authorization = null;
		profile = emptyProfile();
		codeDigits = ['', '', '', ''];
		errorMessage = '';
		savingProfile = false;
	}

	function begin() {
		const stored = getStoredAuthorization();
		if (!stored) {
			resetFlow();
			return;
		}

		authorization = stored;
		if (stored.newUser) {
			profile = { ...emptyProfile(), ...(stored.user || {}) };
			profile.birthday = dateInputValue(stored.user?.birthday);
			stage = 'registration';
		} else {
			emitAuthorized(stored);
		}
	}

	function emitAuthorized(auth) {
		authorization = auth;
		dispatch('authorized', { authorization: auth });
		if (!inline) open = false;
	}

	function close() {
		if (inline) return;
		dispatch('close');
		open = false;
	}

	function handleKeydown(event) {
		if (inline || !open) return;
		if (event.key === 'Escape') {
			event.preventDefault();
			close();
		}
	}

	function dateInputValue(value) {
		if (!value) return '';
		if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return '';
		return date.toISOString().slice(0, 10);
	}

	async function requestCode() {
		errorMessage = '';
		if (!personalDataConsent) {
			errorMessage = 'Для продолжения необходимо дать согласие на хранение и обработку персональных данных.';
			return;
		}
		if (normalizedPhone.length !== 11) {
			errorMessage = 'Введите номер телефона в корректном формате.';
			return;
		}

		stage = 'loading';
		try {
			verification = await apiRequest('auth/request', {
				method: 'POST',
				baseUrl: apiUrl,
				body: { phone: normalizedPhone, provider: Number(provider) }
			});
			provider = String(Number(verification.provider) || Number(provider));
			codeDigits = ['', '', '', ''];
			stage = 'code';
			await tick();
			pinInputs[0]?.focus();
		} catch (error) {
			stage = 'phone';
			errorMessage = error.message;
		}
	}

	function handlePinInput(event, index) {
		const digits = event.currentTarget.value.replace(/\D/g, '');
		if (index === 0 && digits.length > 1) {
			codeDigits = Array.from({ length: 4 }, (_, digitIndex) => digits[digitIndex] || '');
			if (codeDigits.every(Boolean)) {
				void completeAuthorization();
			} else {
				pinInputs[digits.length]?.focus();
			}
			return;
		}
		const value = digits.slice(-1);
		codeDigits[index] = value;
		codeDigits = [...codeDigits];
		if (value && index < codeDigits.length - 1) {
			pinInputs[index + 1]?.focus();
		}
		if (codeDigits.every(Boolean)) void completeAuthorization();
	}

	function handlePinKeydown(event, index) {
		if (event.key === 'Backspace' && !codeDigits[index] && index > 0) {
			pinInputs[index - 1]?.focus();
		}
	}

	function handlePinPaste(event) {
		const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 4);
		if (!pasted) return;
		event.preventDefault();
		codeDigits = Array.from({ length: 4 }, (_, index) => pasted[index] || '');
		if (codeDigits.every(Boolean)) {
			void completeAuthorization();
		} else {
			pinInputs[pasted.length]?.focus();
		}
	}

	async function completeAuthorization() {
		if (!verification || stage === 'loading') return;
		const code = codeDigits.join('');
		if (code.length !== 4) {
			errorMessage = 'Введите 4-значный код подтверждения.';
			return;
		}

		errorMessage = '';
		stage = 'loading';
		try {
			const response = await apiRequest('auth/complete', {
				method: 'POST',
				baseUrl: apiUrl,
				body: { verificationId: verification.verificationId, code }
			});
			storeAuthorization(response);
			if (response.newUser) {
				profile = { ...emptyProfile(), ...(response.user || {}) };
				profile.birthday = dateInputValue(response.user?.birthday);
				stage = 'registration';
			} else {
				emitAuthorized(response);
			}
		} catch (error) {
			stage = 'code';
			errorMessage = error.message;
		}
	}

	function validateBirthDate(value) {
		if (!value) return 'Укажите дату рождения.';
		const date = new Date(`${value}T00:00:00`);
		if (Number.isNaN(date.getTime())) return 'Укажите корректную дату рождения.';
		if (date > new Date()) return 'Дата рождения не может быть в будущем.';
		return '';
	}

	async function finishRegistration() {
		errorMessage = '';
		if (!profile.lastName.trim() || !profile.firstName.trim()) {
			errorMessage = 'Заполните имя и фамилию.';
			return;
		}
		const birthDateError = validateBirthDate(profile.birthday);
		if (birthDateError) {
			errorMessage = birthDateError;
			return;
		}

		savingProfile = true;
		try {
			const user = await apiRequest('auth/fill-user', {
				method: 'POST',
				baseUrl: apiUrl,
				accessToken: authorization.accessToken,
				body: {
					firstName: profile.firstName.trim(),
					lastName: profile.lastName.trim(),
					surName: profile.surName.trim() || null,
					birthDate: `${profile.birthday}T00:00:00.000Z`
				}
			});
			emitAuthorized(storeAuthorization({ ...authorization, user, newUser: false }));
		} catch (error) {
			if (error.status === 401) {
				clearAuthorization();
				resetFlow();
				errorMessage = 'Сессия завершена. Войдите снова.';
			} else {
				errorMessage = error.message;
			}
		} finally {
			savingProfile = false;
		}
	}

	function changePhone() {
		stage = 'phone';
		verification = null;
		codeDigits = ['', '', '', ''];
		errorMessage = '';
	}
</script>

<svelte:window on:keydown={handleKeydown} />

{#if inline || open}
	<div class="auth-dialog" class:auth-dialog--modal={!inline} bind:this={host}>
		{#if !inline}
			<div class="auth-dialog__backdrop" on:click={close}></div>
		{/if}

		<div class="auth-card" class:auth-card--modal={!inline} role={inline ? undefined : 'dialog'} aria-modal={inline ? undefined : 'true'}>
			{#if !inline}
				<button type="button" class="auth-dialog__close" aria-label="Закрыть" on:click={close}>×</button>
			{/if}

			<div class="brand-mark" aria-hidden="true">М</div>
			<p class="eyebrow">{eyebrow}</p>
			<h1>{stage === 'registration' ? 'Завершите регистрацию' : title}</h1>

			{#if stage === 'phone'}
				<p class="intro">{intro}</p>
				<form on:submit|preventDefault={requestCode}>
					<label for="auth-dialog-phone">Номер телефона</label>
					<input
						id="auth-dialog-phone"
						type="tel"
						autocomplete="tel"
						placeholder="+7 (900) 000-00-00"
						bind:value={phone}
						required
					/>

					<label for="auth-dialog-provider">Способ подтверждения</label>
					<WidgetSelect
						className="--black"
						name="auth-dialog-provider"
						options={providers.map((item) => ({ value: String(item.value), label: item.label }))}
						bind:value={provider}
					/>
					<p class="hint">{selectedProvider.hint}</p>
					<div class="consent-row">
						<input
							id="auth-dialog-consent"
							class="consent-checkbox"
							type="checkbox"
							bind:checked={personalDataConsent}
						/>
						<label for="auth-dialog-consent">Даю согласие на хранение и обработку персональных данных</label>
					</div>
					<button
						class="primary-button"
						type="submit"
						disabled={normalizedPhone.length !== 11 || !personalDataConsent}
					>
						Получить код
					</button>
				</form>
			{:else if stage === 'code'}
				<p class="intro">Введите код подтверждения, отправленный через {selectedProvider.label} на номер {phone}.</p>
				<div class="pin-inputs" aria-label="4-значный код подтверждения">
					{#each codeDigits as digit, index}
						<input
							bind:this={pinInputs[index]}
							bind:value={codeDigits[index]}
							type="text"
							inputmode="numeric"
							autocomplete={index === 0 ? 'one-time-code' : 'off'}
							maxlength={index === 0 ? 4 : 1}
							aria-label={`Цифра ${index + 1}`}
							on:input={(event) => handlePinInput(event, index)}
							on:keydown={(event) => handlePinKeydown(event, index)}
							on:paste={handlePinPaste}
						/>
					{/each}
				</div>
				<p class="hint">Введите 4-значный код подтверждения.</p>
				<div class="button-row">
					<button class="secondary-button" type="button" on:click={changePhone}>Изменить номер</button>
					<button class="secondary-button" type="button" on:click={requestCode}>Отправить код снова</button>
				</div>
			{:else if stage === 'loading'}
				<div class="loading-state" role="status" aria-label="Загрузка"><Loader size="48px" /></div>
			{:else}
				<p class="intro">Вы входите впервые. Заполните данные профиля, чтобы завершить регистрацию.</p>
				<form on:submit|preventDefault={finishRegistration}>
					<label for="auth-dialog-last-name">Фамилия <span>*</span></label>
					<input id="auth-dialog-last-name" autocomplete="family-name" bind:value={profile.lastName} required />
					<label for="auth-dialog-first-name">Имя <span>*</span></label>
					<input id="auth-dialog-first-name" autocomplete="given-name" bind:value={profile.firstName} required />
					<label for="auth-dialog-sur-name">Отчество</label>
					<input id="auth-dialog-sur-name" bind:value={profile.surName} />
					<label for="auth-dialog-birthday">Дата рождения <span>*</span></label>
					<input
						id="auth-dialog-birthday"
						type="date"
						max={new Date().toISOString().slice(0, 10)}
						bind:value={profile.birthday}
						required
					/>
					<button class="primary-button" type="submit" disabled={savingProfile}>
						{savingProfile ? 'Сохраняем…' : 'Завершить регистрацию'}
					</button>
				</form>
			{/if}
			{#if errorMessage}<p class="message error" role="alert">{errorMessage}</p>{/if}
		</div>
	</div>
{/if}

<style>
	:host {
		display: block;
		color: #122033;
		font-family: inherit;
	}

	.auth-dialog {
		--account-primary: #00244d;
		--account-muted: #62748a;
	}

	.auth-dialog--modal {
		position: fixed;
		inset: 0;
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
	}

	.auth-dialog__backdrop {
		position: absolute;
		inset: 0;
		background: rgb(8 22 47 / 55%);
		backdrop-filter: blur(2px);
	}

	.auth-card {
		position: relative;
		box-sizing: border-box;
		width: min(100%, 480px);
		padding: 36px;
		border: 1px solid #e1e7ef;
		border-radius: 20px;
		background: #fff;
		box-shadow: 0 18px 55px rgb(18 32 51 / 9%);
		max-height: calc(100vh - 40px);
		overflow-y: auto;
	}

	.auth-card--modal {
		z-index: 1;
		box-shadow: 0 28px 80px rgb(8 22 47 / 28%);
	}

	.auth-dialog__close {
		position: absolute;
		top: 14px;
		right: 14px;
		display: grid;
		width: 32px;
		height: 32px;
		padding: 0;
		place-items: center;
		border: 0;
		border-radius: 50%;
		background: #f1f4f8;
		color: #33465d;
		font-size: 20px;
		line-height: 1;
		cursor: pointer;
		transition: background 150ms ease, color 150ms ease;
	}

	.auth-dialog__close:hover {
		background: #e5ebf3;
		color: #122033;
	}

	.brand-mark {
		display: grid;
		width: 54px;
		height: 54px;
		margin: 0 auto 20px;
		place-items: center;
		border-radius: 16px;
		background: var(--account-primary);
		color: #fff;
		font-size: 26px;
		font-weight: 800;
	}

	h1,
	h2,
	p {
		margin-top: 0;
	}

	h1 {
		margin-bottom: 12px;
		color: var(--account-primary);
		font-size: clamp(24px, 4vw, 32px);
		line-height: 1.2;
	}

	.auth-card h1,
	.auth-card .eyebrow {
		text-align: center;
	}

	.eyebrow {
		margin-bottom: 8px;
		color: #71839a;
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.1em;
		text-transform: uppercase;
	}

	.intro {
		margin-bottom: 24px;
		color: var(--account-muted);
		font-size: 14px;
		line-height: 1.6;
	}

	form {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	label {
		margin-top: 6px;
		color: #33465d;
		font-size: 13px;
		font-weight: 650;
	}

	label span {
		color: #c34646;
	}

	input,
	select {
		box-sizing: border-box;
		width: 100%;
		min-height: 46px;
		padding: 11px 13px;
		border: 1px solid #d6dee8;
		border-radius: 10px;
		outline: none;
		background: #fff;
		color: #122033;
		font: inherit;
		font-size: 14px;
		transition: border-color 150ms ease, box-shadow 150ms ease;
	}

	input:focus,
	select:focus {
		border-color: #24598e;
		box-shadow: 0 0 0 3px rgb(36 89 142 / 12%);
	}

	.hint {
		margin: 2px 0 8px;
		color: var(--account-muted);
		font-size: 12px;
		line-height: 1.5;
	}

	.consent-row {
		display: flex;
		align-items: flex-start;
		gap: 10px;
		margin-top: 8px;
	}

	.consent-row label {
		margin: 0;
		color: var(--account-muted);
		font-size: 12px;
		font-weight: 400;
		line-height: 1.5;
		cursor: pointer;
	}

	input.consent-checkbox {
		flex: 0 0 17px;
		width: 17px;
		min-height: 17px;
		height: 17px;
		margin: 1px 0 0;
		padding: 0;
		border: 0;
		border-radius: 4px;
		accent-color: var(--account-primary);
		cursor: pointer;
	}

	input.consent-checkbox:focus {
		box-shadow: 0 0 0 3px rgb(36 89 142 / 18%);
	}

	button {
		font: inherit;
	}

	.primary-button,
	.secondary-button {
		min-height: 44px;
		padding: 10px 16px;
		border-radius: 10px;
		font-size: 14px;
		font-weight: 650;
		cursor: pointer;
		transition: background 150ms ease, border-color 150ms ease, opacity 150ms ease;
	}

	.primary-button {
		margin-top: 8px;
		border: 1px solid var(--account-primary);
		background: var(--account-primary);
		color: #fff;
	}

	.primary-button:hover:not(:disabled) {
		background: #123e6d;
	}

	.primary-button:disabled {
		cursor: not-allowed;
		opacity: 0.55;
	}

	.secondary-button {
		border: 1px solid #d6dee8;
		background: #fff;
		color: var(--account-primary);
	}

	.secondary-button:hover {
		border-color: #9eafc2;
		background: #f7f9fc;
	}

	.pin-inputs {
		display: flex;
		justify-content: center;
		gap: 10px;
		margin: 12px 0 4px;
	}

	.pin-inputs input {
		width: 54px;
		height: 60px;
		padding: 0;
		border-radius: 12px;
		font-size: 24px;
		font-weight: 700;
		text-align: center;
		caret-color: #24598e;
	}

	.pin-inputs input:focus {
		border-color: #24598e;
		background: #f5f9ff;
		box-shadow: 0 0 0 4px rgb(36 89 142 / 20%);
	}

	.button-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 10px;
		margin-top: 8px;
	}

	.loading-state {
		display: flex;
		min-height: 130px;
		align-items: center;
		justify-content: center;
		gap: 12px;
		color: var(--account-muted);
		font-size: 14px;
	}

	.message {
		margin: 12px 0 0;
		padding: 12px 14px;
		border-radius: 10px;
		font-size: 13px;
		line-height: 1.5;
	}

	.error {
		border: 1px solid #f0caca;
		background: #fff5f5;
		color: #a52f2f;
	}

	@media (max-width: 560px) {
		.auth-card {
			padding: 26px 20px;
		}

		.button-row {
			grid-template-columns: 1fr;
		}
	}
</style>
